"use client"

import AuthBgWrapper from '@/features/auth/components/auth-bg-wrapper'
import AuthCardWrapper from '@/features/auth/components/auth-card-wrapper'
import LoginForm from '@/features/auth/components/forms/login-form/login-form'
import LoginFormProvider from '@/features/auth/components/forms/login-form/login-form-provider'
import { loginScreenBgImage } from '@/features/auth/constants/image'
import React, { useEffect, Suspense } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import { PrivacyPolicyLink } from '@/shared/components/privacy-policy-link'
import { useSession } from '@/features/auth/context/session.context'

function HomePageContent() {
    const searchParams = useSearchParams()
    const router = useRouter()
    const { session, isLoading } = useSession()

    // Extract URL parameters for QR codes and group links
    const region = searchParams.get('region')
    const role = searchParams.get('role') as 'homeowner' | 'volunteer' | null
    const team = searchParams.get('team')

    // Store URL params in sessionStorage for use in registration flow
    useEffect(() => {
        if (region) {
            sessionStorage.setItem('unifiedAuthRegion', region)
            console.log('[LandingPage] Stored region in sessionStorage:', region)
        }
        if (role) {
            sessionStorage.setItem('unifiedAuthRole', role)
            console.log('[LandingPage] Stored role in sessionStorage:', role)
        }
        if (team) {
            sessionStorage.setItem('unifiedAuthTeam', team)
            console.log('[LandingPage] Stored team in sessionStorage:', team)
        }
        console.log('[LandingPage] All stored params:', {
            region: sessionStorage.getItem('unifiedAuthRegion'),
            role: sessionStorage.getItem('unifiedAuthRole'),
            team: sessionStorage.getItem('unifiedAuthTeam'),
        })
    }, [region, role, team])

    useEffect(() => {
        // If there's a token in the URL, redirect to callback page to avoid login page flash
        const token = searchParams.get("token")
        if (token) {
            router.replace(`/auth/callback?token=${token}`)
        }
    }, [searchParams, router])

    // Redirect to dashboard if session is already active
    useEffect(() => {
        if (!isLoading && session) {
            router.replace('/homeowners')
        }
    }, [session, isLoading, router])

    // If there's a token, or the session is loading/active, show a loading state
    const token = searchParams.get("token")
    if (token || isLoading || session) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Redirecting...</p>
                </div>
            </div>
        )
    }

    // Determine header text based on URL params
    const getHeaderTitle = () => {
        if (role === 'homeowner') {
            return 'Welcome!'
        }
        if (role === 'volunteer') {
            return 'Volunteer Registration'
        }
        return 'Welcome!'
    }

    const getHeaderDescription = () => {
        if (role === 'homeowner') {
            return 'Getting help or giving help starts here'
        }
        if (role === 'volunteer') {
            return 'Join our volunteer team and make a difference'
        }
        return 'Getting help or giving help starts here'
    }

    return (
        <AuthBgWrapper image={loginScreenBgImage}>
            <AuthCardWrapper
                headerTitle={getHeaderTitle()}
                headerDescription={getHeaderDescription()}
                afterChildren={
                    <p className="text-xs text-center text-muted-foreground pt-4">
                        <PrivacyPolicyLink className="underline hover:text-foreground" />
                    </p>
                }
            >
                <LoginFormProvider
                    prefilledRegion={region ? parseInt(region) : undefined}
                    prefilledRole={role || undefined}
                    prefilledTeam={team || undefined}
                >
                    <LoginForm />
                </LoginFormProvider>
            </AuthCardWrapper>
        </AuthBgWrapper>
    )
}

function HomePage() {
    return (
        <Suspense fallback={
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        }>
            <HomePageContent />
        </Suspense>
    )
}

export default HomePage
